/**
 * Authorization Codes Repository
 * 
 * Data access layer for Authorization Codes DynamoDB table
 */

import { DeleteCommand, GetCommand, PutCommand, UpdateCommand } from '@aws-sdk/lib-dynamodb';
import { AuthorizationCode } from '../types/dynamodb.types';
import { TABLE_NAMES, dynamoDbClient } from './dynamodb.config';

export class AuthorizationCodesRepository {
  /**
   * Create a new authorization code
   */
  async createAuthorizationCode(authCode: AuthorizationCode): Promise<AuthorizationCode> {
    const command = new PutCommand({
      TableName: TABLE_NAMES.AUTHORIZATION_CODES,
      Item: authCode,
    });

    await dynamoDbClient.send(command);
    return authCode;
  }

  /**
   * Get authorization code
   */
  async getAuthorizationCode(code: string): Promise<AuthorizationCode | null> {
    const command = new GetCommand({
      TableName: TABLE_NAMES.AUTHORIZATION_CODES,
      Key: { code },
    });

    const result = await dynamoDbClient.send(command);
    return (result.Item as AuthorizationCode) || null;
  }

  /**
   * Mark authorization code as used
   */
  async markCodeAsUsed(code: string): Promise<void> {
    const command = new UpdateCommand({
      TableName: TABLE_NAMES.AUTHORIZATION_CODES,
      Key: { code },
      UpdateExpression: 'SET used = :used',
      ExpressionAttributeValues: {
        ':used': true,
      },
    });

    await dynamoDbClient.send(command);
  }

  /**
   * Delete authorization code
   */
  async deleteAuthorizationCode(code: string): Promise<void> {
    const command = new DeleteCommand({
      TableName: TABLE_NAMES.AUTHORIZATION_CODES,
      Key: { code },
    });

    await dynamoDbClient.send(command);
  }

  /**
   * Validate authorization code
   * Returns the code if valid, null if invalid or expired
   */
  async validateAuthorizationCode(code: string): Promise<AuthorizationCode | null> {
    const authCode = await this.getAuthorizationCode(code);
    
    if (!authCode) {
      return null;
    }

    // Check if code is already used
    if (authCode.used) {
      return null;
    }

    // Check if code is expired
    const now = Math.floor(Date.now() / 1000);
    if (authCode.expiresAt < now) {
      return null;
    }

    return authCode;
  }
}

export const authorizationCodesRepository = new AuthorizationCodesRepository();
